import React, { useEffect, useReducer, useRef } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useSectionEditor } from "./SectionEditorContext";

const createHighlight = (index = 0) => ({
  id: `highlight-${index}`,
  label: "Key idea",
  start: 0,
  end: 0,
});

export const createInitialHighlightState = (metadata = {}) => {
  return {
    passage: metadata.passage || "",
    highlights: Array.isArray(metadata.highlights)
      ? metadata.highlights.map((highlight, index) => ({
          ...createHighlight(index),
          ...(highlight || {}),
        }))
      : [],
    accessibilityPreview: Boolean(metadata.accessibilityPreview),
  };
};

export const highlightSectionReducer = (state, action) => {
  switch (action.type) {
    case "HYDRATE": {
      if (!action.payload || typeof action.payload !== "object") {
        return state;
      }
      const nextState = createInitialHighlightState(action.payload);
      const currentSerialized = JSON.stringify(state);
      const nextSerialized = JSON.stringify(nextState);
      return currentSerialized === nextSerialized ? state : nextState;
    }
    case "SET_PASSAGE":
      return { ...state, passage: action.value || "" };
    case "ADD_HIGHLIGHT": {
      const nextIndex = state.highlights.length;
      return {
        ...state,
        highlights: [...state.highlights, createHighlight(nextIndex)],
      };
    }
    case "REMOVE_HIGHLIGHT": {
      const removeIndex = action.index;
      if (removeIndex == null || removeIndex < 0) {
        return state;
      }
      return {
        ...state,
        highlights: state.highlights.filter((_, index) => index !== removeIndex),
      };
    }
    case "UPDATE_HIGHLIGHT": {
      const { index, updates } = action;
      if (
        index == null ||
        index < 0 ||
        index >= state.highlights.length ||
        !updates ||
        typeof updates !== "object"
      ) {
        return state;
      }
      return {
        ...state,
        highlights: state.highlights.map((highlight, idx) =>
          idx === index ? { ...highlight, ...updates } : highlight
        ),
      };
    }
    case "TOGGLE_PREVIEW":
      return {
        ...state,
        accessibilityPreview: !state.accessibilityPreview,
      };
    default:
      return state;
  }
};

const computeHighlightErrors = (state) => {
  const errors = {};
  if (!state.passage || !state.passage.trim()) {
    errors.passage = "Enter the passage learners will review.";
  }

  const highlightErrors = state.highlights.map((highlight) => {
    const itemErrors = {};
    if (!highlight.label || !highlight.label.trim()) {
      itemErrors.label = "Provide a label for the highlight.";
    }
    if (Number(highlight.start) < 0 || Number(highlight.end) < 0) {
      itemErrors.range = "Start and end must be positive.";
    }
    if (Number(highlight.end) <= Number(highlight.start)) {
      itemErrors.range = "End must be greater than start.";
    }
    return itemErrors;
  });

  const hasHighlightErrors = highlightErrors.some(
    (itemErrors) => Object.keys(itemErrors).length > 0
  );

  if (hasHighlightErrors) {
    errors.highlightErrors = highlightErrors;
  }

  return Object.keys(errors).length ? errors : null;
};

const HighlightSectionEditor = () => {
  const { section, updateMetadata, setSectionErrors, sectionErrors } =
    useSectionEditor();
  const [state, dispatch] = useReducer(
    highlightSectionReducer,
    section.metadata,
    createInitialHighlightState
  );
  const serializedRef = useRef(JSON.stringify(state));
  const lastErrorRef = useRef(null);

  useEffect(() => {
    dispatch({ type: "HYDRATE", payload: section.metadata });
  }, [section.metadata]);

  useEffect(() => {
    const serialized = JSON.stringify(state);
    if (serializedRef.current !== serialized) {
      serializedRef.current = serialized;
      updateMetadata({
        passage: state.passage,
        highlights: state.highlights,
        accessibilityPreview: state.accessibilityPreview,
      });
    }
  }, [state, updateMetadata]);

  useEffect(() => {
    const errors = computeHighlightErrors(state);
    const serializedErrors = JSON.stringify(errors || {});
    if (serializedErrors !== lastErrorRef.current) {
      lastErrorRef.current = serializedErrors;
      setSectionErrors(errors);
    }
  }, [state, setSectionErrors]);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h6" component="h3">
            Highlight passage
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Provide the passage and identify exemplar spans learners should
            highlight.
          </Typography>
        </Box>

        <TextField
          label="Passage"
          value={state.passage}
          onChange={(event) =>
            dispatch({ type: "SET_PASSAGE", value: event.target.value })
          }
          multiline
          minRows={4}
          helperText={sectionErrors?.passage}
          error={Boolean(sectionErrors?.passage)}
        />

        <FormControlLabel
          control={
            <Switch
              checked={state.accessibilityPreview}
              onChange={() => dispatch({ type: "TOGGLE_PREVIEW" })}
            />
          }
          label="Show accessibility preview"
        />

        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">Highlight spans</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => dispatch({ type: "ADD_HIGHLIGHT" })}
            >
              Add span
            </Button>
          </Stack>
          {sectionErrors?.highlightErrors && (
            <Typography color="error" variant="body2">
              Review the highlight spans to fix validation errors.
            </Typography>
          )}
          {state.highlights.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Use Add span to identify exemplar highlights for the passage.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {state.highlights.map((highlight, index) => (
                <Paper
                  key={highlight.id || index}
                  variant="outlined"
                  sx={{ p: 2, bgcolor: "background.default" }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="subtitle2">
                        Span {index + 1}
                      </Typography>
                      <IconButton
                        aria-label={`Remove span ${index + 1}`}
                        onClick={() =>
                          dispatch({ type: "REMOVE_HIGHLIGHT", index })
                        }
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Label"
                          value={highlight.label || ""}
                          onChange={(event) =>
                            dispatch({
                              type: "UPDATE_HIGHLIGHT",
                              index,
                              updates: { label: event.target.value },
                            })
                          }
                          fullWidth
                          helperText={
                            sectionErrors?.highlightErrors?.[index]?.label
                          }
                          error={Boolean(
                            sectionErrors?.highlightErrors?.[index]?.label
                          )}
                        />
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <TextField
                          label="Start index"
                          type="number"
                          value={highlight.start}
                          onChange={(event) =>
                            dispatch({
                              type: "UPDATE_HIGHLIGHT",
                              index,
                              updates: { start: Number(event.target.value) },
                            })
                          }
                          fullWidth
                          helperText={
                            sectionErrors?.highlightErrors?.[index]?.range
                          }
                          error={Boolean(
                            sectionErrors?.highlightErrors?.[index]?.range
                          )}
                        />
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <TextField
                          label="End index"
                          type="number"
                          value={highlight.end}
                          onChange={(event) =>
                            dispatch({
                              type: "UPDATE_HIGHLIGHT",
                              index,
                              updates: { end: Number(event.target.value) },
                            })
                          }
                          fullWidth
                          helperText={
                            sectionErrors?.highlightErrors?.[index]?.range
                          }
                          error={Boolean(
                            sectionErrors?.highlightErrors?.[index]?.range
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default HighlightSectionEditor;
